import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Search, User, Plus, DollarSign, Calendar, Wallet, TrendingUp,
  FileText, Receipt, ChevronRight, AlertCircle, ArrowUpFromLine,
  MessageCircle, Download,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { PagoDialog } from './PagoDialog';
import { formatCurrency, formatDate } from './utils';
import api from '../../lib/api';
import { notifyAbonoOnWhatsApp } from '../../lib/whatsapp';
import { exportEstadoCuentaToPDF } from '../../lib/exportUtils';

/**
 * Extrae la descripción "limpia" que digitó el usuario a partir del
 * campo `observaciones` guardado en pagos, quitando los prefijos
 * técnicos `[ABONO:<uuid>]`, `[MON:USD]`, `[TASA:...]`, `[USD:...]`.
 */
const cleanDescripcion = (obs) => {
  if (!obs) return '';
  return String(obs)
    .replace(/\[ABONO:[^\]]+\]/g, '')
    .replace(/\[MON:[^\]]+\]/g, '')
    .replace(/\[TASA:[^\]]+\]/g, '')
    .replace(/\[USD:[^\]]+\]/g, '')
    .trim();
};

/**
 * Extrae el grupo de abono (uuid) del campo observaciones, si existe.
 * Devuelve null si el pago fue registrado directamente contra una factura
 * (flujo antiguo, sin agrupación).
 */
const getAbonoGrupoId = (obs) => {
  if (!obs) return null;
  const m = String(obs).match(/\[ABONO:([^\]]+)\]/);
  return m ? m[1] : null;
};

/**
 * Extrae metadata de moneda/tasa/monto_original desde observaciones.
 * Retorna { moneda, tasa_cambio, monto_original } o valores por defecto DOP.
 */
const getMonedaMeta = (pago) => {
  const obs = pago?.observaciones || '';
  // Preferir campos que el backend ya envía enriquecidos
  const monedaBackend = pago?.moneda;
  if (monedaBackend) {
    return {
      moneda: monedaBackend,
      tasa_cambio: pago.tasa_cambio ?? null,
      monto_original: pago.monto_original ?? null,
    };
  }
  const mMoneda = obs.match(/\[MON:([A-Z]+)\]/);
  const mTasa = obs.match(/\[TASA:([0-9.]+)\]/);
  const mUsd = obs.match(/\[USD:([0-9.]+)\]/);
  return {
    moneda: mMoneda ? mMoneda[1] : 'DOP',
    tasa_cambio: mTasa ? parseFloat(mTasa[1]) : null,
    monto_original: mUsd ? parseFloat(mUsd[1]) : null,
  };
};

/**
 * Tab: Abonos por Cliente.
 * - Barra de búsqueda con autocompletado.
 * - Al seleccionar cliente muestra: resumen, facturas pendientes (más viejas
 *   primero), historial de abonos y botón "Agregar Abono".
 * - El abono se distribuye a las facturas más viejas primero (FIFO).
 */
export const AbonoClienteTab = ({
  clientes,
  onDataChanged,
  comprobantes = [],
  onSelectComprobantes,
  onRemoveComprobante,
  uploadingComprobante = false,
  onClearComprobantes,
}) => {
  const [busqueda, setBusqueda] = useState('');
  const [clienteId, setClienteId] = useState(null);
  const [estadoCuenta, setEstadoCuenta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [abonoOpen, setAbonoOpen] = useState(false);
  const [ultimoAbono, setUltimoAbono] = useState(null); // resumen visible tras aplicar

  // Sugerencias de autocomplete (filtradas por busqueda)
  const sugerencias = useMemo(() => {
    const term = busqueda.trim().toLowerCase();
    if (!term) return [];
    return clientes
      .filter((c) => {
        const nombre = (c.nombre || '').toLowerCase();
        const tel = (c.telefono || '').toLowerCase();
        const email = (c.email || '').toLowerCase();
        return nombre.includes(term) || tel.includes(term) || email.includes(term);
      })
      .slice(0, 8);
  }, [busqueda, clientes]);

  const seleccionarCliente = useCallback(async (c) => {
    setClienteId(c.id || c._id);
    setBusqueda(c.nombre || '');
    setUltimoAbono(null);
  }, []);

  // Cargar estado de cuenta cuando cambia el cliente
  const cargarEstadoCuenta = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await api.get(`/cxc/estado-cuenta/${id}`);
      setEstadoCuenta(res.data);
    } catch (err) {
      toast.error('Error cargando estado de cuenta');
      setEstadoCuenta(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (clienteId) cargarEstadoCuenta(clienteId);
    else setEstadoCuenta(null);
  }, [clienteId, cargarEstadoCuenta]);

  const limpiar = () => {
    setBusqueda('');
    setClienteId(null);
    setEstadoCuenta(null);
    setUltimoAbono(null);
  };

  // Facturas pendientes ordenadas más viejas primero (para preview de aplicación).
  // Desempate por número de factura ASC cuando comparten fecha.
  const facturasPendientes = useMemo(() => {
    if (!estadoCuenta?.facturas) return [];
    return estadoCuenta.facturas
      .filter((f) => ['Pendiente', 'Parcial'].includes(f.estado_pago))
      .sort((a, b) => {
        const df = (a.fecha || '').localeCompare(b.fecha || '');
        if (df !== 0) return df;
        return (a.numero_factura || '').localeCompare(b.numero_factura || '', 'es', { numeric: true });
      });
  }, [estadoCuenta]);

  // Historial: agrupar pagos por abono_grupo_id (cuando existe). Los pagos sin
  // grupo se muestran como abonos independientes (compat con flujo antiguo).
  const historialAbonos = useMemo(() => {
    const pagos = estadoCuenta?.pagos || [];
    const groups = new Map();
    pagos.forEach((p) => {
      const gid = getAbonoGrupoId(p.observaciones);
      const key = gid || `single-${p.id}`;
      if (!groups.has(key)) {
        groups.set(key, {
          id: key,
          es_grupo: !!gid,
          fecha: p.fecha,
          descripcion: cleanDescripcion(p.observaciones),
          metodo_pago: p.metodo_pago,
          referencia: p.referencia,
          items: [],
        });
      }
      const g = groups.get(key);
      g.items.push(p);
      // Actualizar fecha más reciente del grupo (usar la primera vista, que ya vino desc)
      if (!g.fecha || (p.fecha || '') > g.fecha) g.fecha = p.fecha;
    });
    // Ordenar por fecha desc
    return Array.from(groups.values()).sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''));
  }, [estadoCuenta]);

  const saldoTotalPendiente = useMemo(() => {
    return facturasPendientes.reduce((acc, f) => acc + Number(f.saldo || 0), 0);
  }, [facturasPendientes]);

  return (
    <div className="space-y-4">
      {/* Barra de búsqueda */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Search className="w-5 h-5" /> Buscar Cliente
          </CardTitle>
          <CardDescription>
            Escribe el nombre, teléfono o email para encontrar al cliente y gestionar sus abonos.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Ej: Adrián Linares, 809-555-1234..."
              value={busqueda}
              onChange={(e) => { setBusqueda(e.target.value); if (clienteId) setClienteId(null); }}
              className="h-11 pl-9"
              data-testid="input-busqueda-cliente"
            />
            {/* Dropdown de sugerencias */}
            {sugerencias.length > 0 && !clienteId && (
              <div className="absolute z-50 mt-1 w-full max-h-80 overflow-y-auto rounded-md border bg-popover shadow-lg">
                {sugerencias.map((c) => (
                  <button
                    key={c.id || c._id}
                    type="button"
                    onClick={() => seleccionarCliente(c)}
                    className="w-full text-left px-3 py-2 hover:bg-accent transition-colors flex items-center gap-2 border-b border-border/40 last:border-0"
                    data-testid={`sugerencia-${c.id || c._id}`}
                  >
                    <User className="w-4 h-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{c.nombre}</div>
                      {(c.telefono || c.email) && (
                        <div className="text-[11px] text-muted-foreground truncate">
                          {[c.telefono, c.email].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </button>
                ))}
              </div>
            )}
          </div>
          {clienteId && (
            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="secondary" className="gap-1">
                <User className="w-3 h-3" /> {estadoCuenta?.cliente?.nombre || busqueda}
              </Badge>
              <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={limpiar}>
                cambiar cliente
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contenido cuando hay cliente seleccionado */}
      {loading && clienteId && (
        <div className="text-center py-10 text-muted-foreground">
          Cargando estado de cuenta…
        </div>
      )}

      {!loading && clienteId && estadoCuenta && (
        <>
          {/* Resumen y acción */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="pt-4">
                <div className="flex items-center gap-1 text-[10px] uppercase text-muted-foreground font-semibold tracking-wide">
                  <Wallet className="w-3 h-3" /> Saldo pendiente
                </div>
                <div
                  className="text-2xl font-bold tabular-nums mt-1"
                  style={{ fontFamily: 'Space Grotesk' }}
                  data-testid="saldo-pendiente-cliente"
                >
                  {formatCurrency(saldoTotalPendiente)}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {facturasPendientes.length} factura{facturasPendientes.length === 1 ? '' : 's'} pendiente{facturasPendientes.length === 1 ? '' : 's'}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-1 text-[10px] uppercase text-muted-foreground font-semibold tracking-wide">
                  <TrendingUp className="w-3 h-3" /> Total pagado
                </div>
                <div
                  className="text-2xl font-bold tabular-nums mt-1 text-emerald-700"
                  style={{ fontFamily: 'Space Grotesk' }}
                >
                  {formatCurrency(estadoCuenta.resumen?.total_pagado || 0)}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {estadoCuenta.resumen?.num_pagos || 0} pago{(estadoCuenta.resumen?.num_pagos || 0) === 1 ? '' : 's'}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-1 text-[10px] uppercase text-muted-foreground font-semibold tracking-wide">
                  <FileText className="w-3 h-3" /> Total facturado
                </div>
                <div
                  className="text-2xl font-bold tabular-nums mt-1"
                  style={{ fontFamily: 'Space Grotesk' }}
                >
                  {formatCurrency(estadoCuenta.resumen?.total_facturado || 0)}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {estadoCuenta.resumen?.num_facturas || 0} factura{(estadoCuenta.resumen?.num_facturas || 0) === 1 ? '' : 's'}
                </div>
              </CardContent>
            </Card>
            <Card className="flex flex-col justify-center items-center border-dashed gap-2 p-3">
              <Button
                onClick={() => setAbonoOpen(true)}
                disabled={saldoTotalPendiente <= 0}
                className="w-full h-14 text-base gap-2"
                data-testid="btn-agregar-abono"
              >
                <Plus className="w-5 h-5" /> Registrar Pago
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-2"
                onClick={() => {
                  try {
                    exportEstadoCuentaToPDF(estadoCuenta, { mode: 'save' });
                    toast.success('Estado de cuenta descargado');
                  } catch (e) {
                    toast.error('No se pudo generar el PDF');
                  }
                }}
                data-testid="btn-descargar-estado"
              >
                <Download className="w-4 h-4" /> Descargar Estado PDF
              </Button>
            </Card>
          </div>

          {/* Aviso último abono aplicado */}
          {ultimoAbono && (
            <Card className="border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start gap-2">
                  <ArrowUpFromLine className="w-5 h-5 text-emerald-700 mt-0.5" />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold text-emerald-900 dark:text-emerald-200">
                      Abono de {formatCurrency(ultimoAbono.monto_aplicado)} distribuido:
                    </div>
                    <ul className="mt-1 space-y-0.5">
                      {ultimoAbono.aplicaciones.map((a) => (
                        <li key={a.factura_id} className="text-emerald-800 dark:text-emerald-300 text-xs">
                          • <b>{a.factura_numero}</b> ({formatDate(a.fecha_factura)}): {formatCurrency(a.monto_aplicado)} aplicado
                          {' '}→ saldo {formatCurrency(a.saldo_restante)}{' '}
                          <Badge variant={a.estado_pago_nuevo === 'Pagada' ? 'default' : 'secondary'} className="text-[10px] px-1 py-0 ml-1">
                            {a.estado_pago_nuevo}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                    {ultimoAbono.descripcion && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Descripción: {ultimoAbono.descripcion}
                      </div>
                    )}
                    {/* Notificar por WhatsApp */}
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      {(estadoCuenta?.cliente?.telefono || estadoCuenta?.cliente?.celular) ? (
                        <Button
                          size="sm"
                          className="bg-[#25D366] hover:bg-[#1DA851] text-white gap-2"
                          onClick={() => {
                            const ok = notifyAbonoOnWhatsApp({
                              cliente: estadoCuenta?.cliente,
                              abono: ultimoAbono,
                            });
                            if (!ok) toast.error('El teléfono del cliente no es válido para WhatsApp');
                          }}
                          data-testid="btn-whatsapp-abono"
                        >
                          <MessageCircle className="w-4 h-4" />
                          Enviar por WhatsApp
                        </Button>
                      ) : (
                        <div className="text-[11px] text-muted-foreground italic flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Este cliente no tiene teléfono registrado para WhatsApp.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Facturas pendientes ordenadas más viejas primero */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                <FileText className="w-4 h-4" /> Facturas Pendientes
                <Badge variant="outline" className="ml-1 text-[10px]">Más viejas primero</Badge>
              </CardTitle>
              <CardDescription className="text-xs">
                Los abonos se aplican primero a la factura más vieja hasta cubrirla, luego pasan a la siguiente.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {facturasPendientes.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground text-sm">
                  Este cliente no tiene facturas pendientes.
                </div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>Factura</TableHead>
                        <TableHead>Fecha</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Saldo</TableHead>
                        <TableHead className="text-center">Estado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {facturasPendientes.map((f, idx) => (
                        <TableRow key={f.id} data-testid={`fact-pendiente-${f.id}`}>
                          <TableCell className="text-muted-foreground text-xs w-8">{idx + 1}</TableCell>
                          <TableCell className="font-semibold">{f.numero_factura}</TableCell>
                          <TableCell className="text-xs">{formatDate(f.fecha)}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatCurrency(f.total_monto)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums font-semibold">
                            {formatCurrency(f.saldo)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge
                              variant={f.estado_pago === 'Parcial' ? 'secondary' : 'outline'}
                              className="text-[10px]"
                            >
                              {f.estado_pago}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Historial de abonos */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                <Receipt className="w-4 h-4" /> Historial de Abonos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {historialAbonos.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground text-sm">
                  Este cliente aún no ha realizado abonos.
                </div>
              ) : (
                <div className="space-y-3">
                  {historialAbonos.map((g) => {
                    const totalGrupo = g.items.reduce((acc, p) => acc + Number(p.monto || 0), 0);
                    const meta = getMonedaMeta(g.items[0]);
                    const esUsd = meta.moneda === 'USD' && meta.monto_original && meta.tasa_cambio;
                    return (
                      <div
                        key={g.id}
                        className="rounded-md border p-3 bg-accent/30"
                        data-testid={`abono-grupo-${g.id}`}
                      >
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className="gap-1">
                              <Calendar className="w-3 h-3" /> {formatDate(g.fecha)}
                            </Badge>
                            <Badge variant="outline" className="gap-1">
                              <DollarSign className="w-3 h-3" /> {g.metodo_pago}
                            </Badge>
                            {esUsd && (
                              <Badge className="gap-1 bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-100">
                                USD {Number(meta.monto_original).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} × {Number(meta.tasa_cambio).toFixed(2)}
                              </Badge>
                            )}
                            {g.referencia && (
                              <Badge variant="outline" className="text-[10px]">Ref: {g.referencia}</Badge>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold tabular-nums text-emerald-700" style={{ fontFamily: 'Space Grotesk' }}>
                              {formatCurrency(totalGrupo)}
                            </div>
                            {esUsd && (
                              <div className="text-[10px] text-muted-foreground tabular-nums">
                                Equivalente DOP
                              </div>
                            )}
                          </div>
                        </div>
                        {g.descripcion && (
                          <div className="mt-2 text-xs text-muted-foreground italic border-l-2 border-primary/30 pl-2">
                            &ldquo;{g.descripcion}&rdquo;
                          </div>
                        )}
                        <Separator className="my-2" />
                        <div className="text-[11px] uppercase text-muted-foreground font-semibold tracking-wide mb-1">
                          Facturas pagadas
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {g.items.map((p) => (
                            <div
                              key={p.id}
                              className="flex items-center justify-between text-xs bg-background/60 rounded px-2 py-1"
                            >
                              <span className="font-medium">{p.factura_numero}</span>
                              <span className="tabular-nums font-semibold text-emerald-700">
                                {formatCurrency(p.monto)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Empty state — sin cliente seleccionado */}
      {!clienteId && !busqueda && (
        <Card className="border-dashed">
          <CardContent className="py-10 text-center text-muted-foreground">
            <User className="w-12 h-12 mx-auto opacity-30 mb-3" />
            <p className="text-sm">Empieza escribiendo el nombre de un cliente para ver sus abonos y facturas.</p>
          </CardContent>
        </Card>
      )}

      {!clienteId && busqueda && sugerencias.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center text-muted-foreground">
            <AlertCircle className="w-10 h-10 mx-auto opacity-40 mb-2" />
            <p className="text-sm">Sin resultados para &ldquo;{busqueda}&rdquo;.</p>
          </CardContent>
        </Card>
      )}

      {/* Modal para registrar pago (unifica registrar pago con abono por cliente) */}
      <PagoDialog
        open={abonoOpen}
        onOpenChange={(o) => {
          setAbonoOpen(o);
          if (!o && typeof onClearComprobantes === 'function') onClearComprobantes();
        }}
        clientes={clientes}
        initialClienteId={clienteId}
        comprobantes={comprobantes}
        onSelectComprobantes={onSelectComprobantes}
        onRemoveComprobante={onRemoveComprobante}
        uploadingComprobante={uploadingComprobante}
        onSaved={async (data) => {
          if (data) setUltimoAbono(data);
          if (typeof onClearComprobantes === 'function') onClearComprobantes();
          await cargarEstadoCuenta(clienteId);
          if (typeof onDataChanged === 'function') onDataChanged();
        }}
      />
    </div>
  );
};

export default AbonoClienteTab;
